'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import { FaRegHeart } from 'react-icons/fa6'

import {
  FaAngleDoubleRight,
  FaFilm,
  FaHeart,
  FaLock,
  FaMagic,
  FaMarker,
  FaPlay,
  FaTrash,
} from 'react-icons/fa'
import { MdBarChart } from 'react-icons/md'

import { useTranslation } from 'react-i18next'
import {
  BsChatDotsFill,
  BsFillBadgeHdFill,
  BsPersonBoundingBox,
  BsStack,
} from 'react-icons/bs'
import OverlayActionsMenu, { OVERLAY_PILL } from './OverlayActionsMenu'
import HoverVideoPreview from './HoverVideoPreview'
import { useAuth } from '../contexts/AuthContext'
import { CreateContext } from '../contexts/CreateContext'
import { ImageContext } from '../contexts/ImageContext'
import {
  aspectFromRatio,
  formatNumber,
  getModelName,
  isSafeCountry,
  truncate,
} from '../helpers/helpers'
import { safeGetItem, safeSetItem } from '../helpers/safeStorage'
import { useVideoAutoplayPreference } from '../helpers/videoAutoplayPreference'
import {
  isCurrentVideoVersion,
  videoSettingsToClone,
  videoVersionFromDoc,
  videoVersionLabel,
} from '../helpers/videoVersions'
import { t } from 'i18next'
import { HiSpeakerWave } from 'react-icons/hi2'
import useIntersectionObserver from '../hooks/useIntersectionObserver'
import * as socialApi from '../lib/api/social.ts'
import { deleteMedia } from '../app/(chatAndCreateContext)/generate/api'
import * as moderationApi from '../lib/api/moderation.ts'
import useStaffTools from '../hooks/useStaffTools'
import {
  defaultCardImageSizes,
  getOptimizedLocalSrc,
} from '../helpers/cardImage'

const Card = ({
  imageData,
  id,
  title = '',
  imageUrl,
  description = '',
  likes,
  comments,
  uid,
  popImage,
  model = '',
  width,
  height,
  creativity,
  negativePrompt,
  loras,
  views,
  upscaled,
  seed,
  upgradeModal,
  setUpgradeModal,
  profile,
  selectedImageIds,
  setSelectedImageIds,
  disabled,
  quality,
  recommended,
  hideMetrics,
  landingPage = false,
  // Static acquisition galleries use the real card, with every affordance
  // leading to signup and no authenticated product actions.
  signupOnly = false,
  shouldLazyLoad = false,
  imageSizes = defaultCardImageSizes,
  nsfw = false,
  // When provided (mobile/tablet grids), a tap opens the fullscreen ImmersiveFeed
  // instead of navigating to /image/{id}. Omitted elsewhere → unchanged behavior.
  onOpen,
  // When true (long feeds: Explore/Profile), the remote image only loads while
  // within ~1.5 viewports of the screen and its src is dropped when far away, so
  // decoded-image memory stays bounded. Off by default → other callers unchanged.
  windowed = false,
  // When true, the hover overlay chrome (caption, badges, metrics, action menu)
  // is hidden below `md` (phones only) — a tap opens the fullscreen viewer
  // instead, so the grid stays clean (no tap-triggered sticky-hover flash).
  // Medium screens and up keep the hover overlays.
  hideOverlaysMobile = false,
  // Instagram-style video tiles (big paginated feeds: Explore/Profile main grid).
  // Off by default → videos autoplay in view as before (Featured strip, landings,
  // related-image grids). When true, a video tile shows its first frame + a play
  // badge and does NOT autoplay: on hover-capable devices it previews on hover; on
  // touch it never loads the clip inline (a tap opens the fullscreen player).
  // Overridable per-device from Settings → "Autoplay videos" (see the preference
  // hook below), which puts these tiles back on play-in-view for people who want
  // scroll-and-they-play browsing.
  previewMode = false,
  // Curated/Featured strip: autoplay via a single native <video> with a `poster`
  // attribute so the browser hands off poster→clip with no React state flip (kills
  // the grey pop-in the two-layer preview path can show). Ignored when previewMode.
  autoplay = false,
  // Optional fetch priority for the tile's picture or video poster ('high' for the
  // first screen of a long grid). Omitted → the browser's default, as before.
  fetchPriority,
}) => {
  const { i18n } = useTranslation()

  const router = useRouter()
  const { setUserLikedImages, setUserImages } = useContext(ImageContext)

  const {
    setCharImage,
    registerCharImageId,
    setVideoRefImage,
    setVideoRefId,
    setVideoRefLora,
    setFormatTab,
    setVideoStartingPoint,
  } = useContext(CreateContext)

  const [imageLoaded, setImageLoaded] = useState(signupOnly)
  // Windowing: whether this tile is within the load window. Always true when not
  // windowed, so the default render path is untouched.
  const [nearWindow, setNearWindow] = useState(!windowed)
  const { user: authenticatedUser, safe } = useAuth()
  const user = signupOnly ? null : authenticatedUser
  // The trash on someone else's picture: an admin's, by v2's roles on v2, by the build's admin uid on v1.
  const { admin: canRemoveOthers } = useStaffTools(user)
  // In safe countries Safe Mode can't be turned off (no toggle in Settings), so
  // the lock label must not imply a setting the user can go and change.
  const safeCountry = isSafeCountry()
  const [userHasLiked, setUserHasLiked] = useState(false)
  const [currentLikes, setCurrentLikes] = useState(likes < 0 ? 0 : likes)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isBroken, setIsBroken] = useState(false)
  const [isLikeLoading, setIsLikeLoading] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [editedImageUrl, setEditedImageUrl] = useState(() =>
    signupOnly
      ? imageUrl?.replace(
          'bucket.promptchan.ai',
          'bucket.aicloudnetservices.com',
        )
      : undefined,
  )
  // Video-tile layers: `videoReady` fades the clip in over the poster once it has a
  // frame; `posterBroken` drops a failed thumbnail so we fall back to the clip's own
  // first frame (without nuking the card).
  const [videoReady, setVideoReady] = useState(false)
  const [posterBroken, setPosterBroken] = useState(false)
  // Whether this device can hover (desktop-ish). Drives preview-on-hover; touch
  // devices show the first frame + a play badge and open the player on tap.
  const [canHover, setCanHover] = useState(false)
  // Poster preload latch: a windowed video tile loads its poster once it comes within
  // the ~1.5-screen margin and KEEPS it loaded — so scrolling away and back shows it
  // instantly from cache instead of going grey.
  const [posterSeen, setPosterSeen] = useState(!windowed)

  // Settings → "Autoplay videos": opt-in that turns preview tiles back into
  // play-in-view tiles on this device (touch included). False until it resolves
  // after mount, so the default render path is unchanged.
  const autoplayPreference = useVideoAutoplayPreference()

  const videoRef = useRef(null)
  const containerRef = useIntersectionObserver(setIsInView)

  // Windowed feeds: observe the tile with a generous margin so images preload a
  // bit ahead and unload once well past. `shouldLoad` gates the remote <img> src.
  useEffect(() => {
    if (!windowed) return
    const el = containerRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => setNearWindow(entry.isIntersecting),
      { rootMargin: '150% 0px 150% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [windowed, containerRef])

  const shouldLoad = !windowed || nearWindow

  // When a windowed IMAGE tile scrolls far away, drop the loaded flag so the
  // placeholder shows again (and the <img> src is released below). Video tiles are
  // EXCLUDED: their poster src never changes, so resetting this leaves the (already
  // loaded) poster stuck at opacity-0 on scroll-back — the grey-on-return bug.
  useEffect(() => {
    if (windowed && !nearWindow && !imageData?.video) setImageLoaded(false)
  }, [windowed, nearWindow, imageData?.video])

  // Latch the video poster once it's near — loaded ahead of the viewport and never
  // released, so scrolling back up is instant instead of grey.
  useEffect(() => {
    if (nearWindow) setPosterSeen(true)
  }, [nearWindow])

  const isChecked = selectedImageIds?.includes(id)

  useEffect(() => {
    setEditedImageUrl(
      imageUrl?.replace(
        'bucket.promptchan.ai',
        'bucket.aicloudnetservices.com',
      ),
    )
  }, [imageUrl])

  // Detect hover capability (client-only, so no SSR/hydration mismatch — starts
  // false and resolves after mount). `(hover: hover) and (pointer: fine)` ≈ a real
  // mouse; touch/stylus devices report false and get the tap-to-open behavior.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)')
    const apply = () => setCanHover(mq.matches)
    apply()
    mq.addEventListener?.('change', apply)
    return () => mq.removeEventListener?.('change', apply)
  }, [])

  // ── Video tile playback ──────────────────────────────────────────────────────
  // Poster source: the backend stores a compressed first-frame `videoThumbnail` (and
  // a hardened permanent `original_image_url`) for public videos — prefer those as a
  // cheap, instant poster. The host-swap is a no-op on the permanent storage URLs.
  const posterSrc =
    !posterBroken && imageData?.video
      ? (imageData?.videoThumbnail || imageData?.original_image_url)?.replace(
          'bucket.promptchan.ai',
          'bucket.aicloudnetservices.com',
        ) || null
      : null
  const hasPoster = !!posterSrc
  // Play in view for autoplay grids; only on hover (desktop) for preview grids —
  // unless this device opted into autoplay, which puts preview grids on play-in-view
  // too (the clip is still fetched only when it's about to play, and paused/released
  // by the windowing below once the tile leaves the screen).
  const shouldPlayVideo =
    !signupOnly &&
    !!imageData?.video &&
    (previewMode
      ? autoplayPreference
        ? isInView
        : canHover && isHovered
      : isInView || !user)
  // Load the CLIP when it's actually going to play, OR — for older videos with no
  // poster image — to show its own first frame (AHEAD of the viewport: windowed feeds
  // use their ~1.5-screen `nearWindow` margin, others stay lazy at in-view). With a
  // poster present, touch preview tiles fetch only the small thumbnail, never the mp4.
  const loadAhead = windowed ? nearWindow : isInView
  const videoSrc =
    imageData?.video &&
    (shouldPlayVideo || (!hasPoster && (loadAhead || !user)))
      ? editedImageUrl
      : null
  // Show the clip layer only while it's the star: playing over the poster, or — with
  // no poster — as soon as it has decoded its own first frame.
  const showClip =
    !!imageData?.video &&
    (hasPoster ? shouldPlayVideo && videoReady : imageLoaded)
  // Play badge marks a video tile whenever it isn't actively playing the clip.
  const showPlayBadge = previewMode && !!imageData?.video && !shouldPlayVideo
  // Latest values read inside the (re-used) media event handlers without re-binding.
  const shouldPlayRef = useRef(shouldPlayVideo)
  shouldPlayRef.current = shouldPlayVideo
  const hasPosterRef = useRef(hasPoster)
  hasPosterRef.current = hasPoster

  const handleCheckboxToggle = () => {
    if (isChecked) {
      setSelectedImageIds((prevIds) =>
        prevIds.filter((imageId) => imageId !== id),
      )
    } else {
      setSelectedImageIds((prevIds) => [...prevIds, id])
    }
  }

  // Play/pause the clip. `src` is bound declaratively below (loads the first frame
  // once on-screen); here we only start/stop playback: autoplay grids play in view,
  // preview grids play on hover, and when neither we pause and rest on the first
  // frame (nudged to 0.05s in the loadedmetadata handler so iOS renders it instead
  // of a black box). Runs when the intent or the src changes.
  useEffect(() => {
    if (!imageData?.video) return
    const el = videoRef.current
    if (!el) return
    if (shouldPlayVideo) {
      el.play?.().catch(() => {})
    } else {
      el.pause?.()
      try {
        el.currentTime = 0.05
      } catch {
        /* ignore */
      }
    }
  }, [shouldPlayVideo, videoSrc, imageData?.video])

  // (1) Standalone function to fetch “liked” status only when called
  const fetchLikedStatus = async () => {
    if (!user || profile || !isInView || hasFetched) return

    // A feed v2 answered already says whether the person likes it: no call per card.
    if (typeof imageData?.liked_by_me === 'boolean') {
      setUserHasLiked(imageData.liked_by_me)
      setHasFetched(true)
      return
    }

    try {
      const idToken = await user.getIdToken()
      const response = await socialApi.checkLiked(idToken, id)

      if (!response.ok) {
        throw new Error('Failed to fetch liked image status')
      }

      const data = await response.json()
      setUserHasLiked(data.liked)
      setHasFetched(true)
    } catch (error) {
      console.error('Error fetching liked image status:', error)
    }
  }

  // (2) Call fetchLikedStatus from handleMouseEnter
  const handleMouseEnter = () => {
    setIsHovered(true)
    if (isInView && !hasFetched) {
      fetchLikedStatus()
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  const useChat = async () => {
    if (typeof window === 'undefined') return
    try {
      safeSetItem('chatImage', imageData?.original_image_url ?? editedImageUrl)
      safeSetItem('chatId', imageData?.id)
      safeSetItem('chatImageModel', imageData?.model || '')
      router.push('/chat')
    } catch (error) {
      alert(error.message)
    }
  }

  const handleClone = () => {
    if (typeof window === 'undefined') return
    const prompt = getPrompt()
    const negativePrompt = getNegativePrompt()

    if (imageData?.video) {
      // Clone the video's prompt + settings only — do NOT copy the source video's
      // first frame. Clear any ref image/id sitting in context (the root-level
      // CreateProvider persists it across navigation) and switch the panel to
      // text-to-video so the regeneration starts from the prompt, not a baked frame.
      setVideoRefImage(null)
      setVideoRefId(null)
      setVideoStartingPoint('prompt')
      safeSetItem(
        'cloneData',
        JSON.stringify({
          videoModel: imageData.model,
          videoImageSize: `${imageData.width}x${imageData.height}`,
          videoPrompt: prompt,
          videoNegativePrompt: negativePrompt,
          videoLora: imageData.loras ?? imageData.loras,
          seed: imageData.seed,
          cameraMotion: imageData.camera_motion || 'still',
          formatTab: 'video',
          // Video docs store width/height, not a literal `aspect` — derive the orientation
          // so a cloned clip comes back in the shape it was generated in.
          aspect:
            imageData.aspect ??
            aspectFromRatio(imageData.width, imageData.height),
          // Version (as the string the submit endpoint takes — a bare numeric `version` is not
          // a valid `video_version`) plus every v4-voice setting, so the clone reproduces the
          // clip it came from rather than a default-settings render of the same prompt.
          ...videoSettingsToClone(imageData),
          age_slider_video: imageData.age_slider ?? null,
          weight_slider_video: imageData.weight_slider ?? null,
          breast_slider_video: imageData.breast_slider ?? null,
          ass_slider_video: imageData.ass_slider ?? null,

          loop: imageData.loop ? 'Loop' : 'Repeat',
          videoFramerateV2: imageData.frame_rate ?? 25,
          videoQuality: imageData.quality ?? 'Standard',
        }),
      )
    } else {
      safeSetItem(
        'cloneData',
        JSON.stringify({
          model: imageData.model,
          imageSize: `${imageData.width}x${imageData.height}`,
          creativity: imageData.creativity ? imageData.creativity : 50,
          prompt,
          negativePrompt,
          loras: imageData.loras ? imageData.loras : 'Default',
          lorasList: imageData.lorasList ? imageData.lorasList : [],
          seed: imageData.seed,
          filter: imageData.filter ? imageData.filter : 'Default',
          details: imageData.details ? imageData.details : 0,
          emotion: imageData.expression ? imageData.expression : 'Default',
          formatTab: 'photo',
          age_slider: imageData.age_slider ?? null,
          weight_slider: imageData.weight_slider ?? 0,
          breast_slider: imageData.breast_slider ?? 0,
          ass_slider: imageData.ass_slider ?? 0,
          aspect: imageData?.aspect,
        }),
      )
    }

    router.push('/generate')
  }

  // Tweak (Qwen-Edit) hand-off: /generate reads `tweakData` on mount and opens
  // the tweak modal for this image. Images only — tweak has no video path.
  const handleTweak = () => {
    safeSetItem(
      'tweakData',
      JSON.stringify({
        imageId: id,
        imageUrl: imageData?.original_image_url ?? editedImageUrl,
      }),
    )
    router.push('/generate')
  }

  const handleExtend = () => {
    if (typeof window === 'undefined') return
    const storedCloneData = safeGetItem('cloneData')
    const cloneData = storedCloneData ? JSON.parse(storedCloneData) : {}

    const modelMap = {
      Real: 'Real',
      Hyperreal: 'Hyperreal',
      Anime: 'Anime',
      Hyperanime: 'Anime',
      'K-Pop': 'Real',
      Fur: 'Hyperreal',
    }

    safeSetItem(
      'cloneData',
      JSON.stringify({
        ...cloneData,
        formatTab: 'video',
        videoModel: modelMap[imageData.model] ?? imageData.model,
      }),
    )
    safeSetItem('extendVideoId', imageData.id)
    // The extend modal offers a spoken line only for v5 sources, and defaults to v4 when this is
    // absent — so without it, extending a v4-voice clip from a card could not speak at all.
    safeSetItem('extendVideoVersion', videoVersionFromDoc(imageData))

    router.push('/generate')
  }

  const useAnimate = async () => {
    if (typeof window === 'undefined') return
    try {
      const prompt = getPrompt()
      const negativePrompt = getNegativePrompt()

      setVideoRefImage(imageData.original_image_url ?? editedImageUrl)
      setVideoRefId(imageData?.id)
      setVideoRefLora(imageData.loras)
      setVideoStartingPoint('image')

      const storedCloneData = safeGetItem('cloneData')
      let cloneData = storedCloneData ? JSON.parse(storedCloneData) : {}

      const modelMap = {
        Real: 'Real',
        Hyperreal: 'Hyperreal',
        Anime: 'Anime',
        Hyperanime: 'Anime',
        'K-Pop': 'Real',
        Fur: 'Hyperreal',
      }

      const mappedModel = modelMap.hasOwnProperty(imageData.model)
        ? modelMap[imageData.model]
        : imageData.model

      cloneData = {
        ...cloneData,
        videoPrompt: prompt,
        videoNegativePrompt: negativePrompt,
        videoModel: mappedModel,
        videoLora: 'Default',
        formatTab: 'video',
        videoImageSize: `${imageData.width}x${imageData.height}`,
        videoId: id,
        // Auto-start image-to-video on arrival (new UI has no Animate button).
        autoAnimate: true,
      }

      safeSetItem('cloneData', JSON.stringify(cloneData))

      router.push('/generate')
    } catch {
      // setErrorText(error.message)
    }
  }

  const useCharacter = async () => {
    try {
      const charUrl = imageData.original_image_url ?? editedImageUrl
      setCharImage(charUrl)
      // Record which doc the character URL belongs to so generation can save
      // the back-reference (feed cards carry raw doc fields: original_url is
      // the source doc id matching original_image_url).
      registerCharImageId(
        charUrl,
        imageData.original_image_url ? imageData.original_url : imageData.id,
      )
      setFormatTab('photo')
      router.push('/generate')
    } catch {
      // setErrorText(error.message)
    }
  }

  const handleModel = (model) => {
    if (typeof window === 'undefined') return
    safeSetItem(
      'cloneData',
      JSON.stringify({
        model,
      }),
    )
    router.push('/generate')
  }

  const deleteImage = async () => {
    if (!user) return

    setIsDeleting(true)

    const idToken = await user.getIdToken()

    try {
      // One of v2's (`m_…`) is deleted on v2, v1's on v1 (generate/api.ts). An admin's delete of someone else's is
      // moderation's (moderation.ts): on v2, staff's removal.
      const target = { imageId: id, imageUid: uid }
      const response =
        uid === user.uid
          ? await deleteMedia(idToken, target)
          : await moderationApi.removeOthers(idToken, target)

      if (response.ok) {
        setUserImages((prevImages) => {
          const updatedImages = { ...prevImages }
          updatedImages[uid] = updatedImages[uid]?.filter(
            (img) => img.id !== id,
          )
          return updatedImages
        })

        setUserLikedImages((prevImages) => {
          const updatedImages = { ...prevImages }
          updatedImages[uid] = updatedImages[uid]?.filter(
            (img) => img.id !== id,
          )
          return updatedImages
        })

        popImage(id)
      } else {
        console.error('Failed to delete the image')
      }
    } catch (error) {
      console.error('Error deleting the image:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleImageError = async () => {
    setIsBroken(true)
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  const toggleLike = async () => {
    if (isLikeLoading) return

    setIsLikeLoading(true)

    const newStatus = !userHasLiked
    setUserHasLiked(newStatus)

    const newLikes = newStatus ? currentLikes + 1 : currentLikes - 1
    const updatedImageData = {
      ...imageData,
      likes: newLikes,
    }
    setCurrentLikes(newLikes)

    if (!user) return

    const idToken = await user.getIdToken()

    try {
      const response = await socialApi.setLiked(
        idToken,
        updatedImageData,
        newStatus,
      )

      if (response.ok) {
        setUserLikedImages((prevState) => {
          const userLikedImagesCopy = { ...prevState }
          if (!userLikedImagesCopy[user?.uid]) {
            userLikedImagesCopy[user?.uid] = []
          }
          if (newStatus) {
            const updatedCardDataCopy = {
              ...updatedImageData,
              title,
              description,
              imageUrl: editedImageUrl,
            }
            userLikedImagesCopy[user?.uid].unshift(updatedCardDataCopy)
          } else {
            const indexToRemove = userLikedImagesCopy[user?.uid].findIndex(
              (img) => img.id === id,
            )
            if (indexToRemove !== -1) {
              userLikedImagesCopy[user?.uid].splice(indexToRemove, 1)
            }
          }
          return userLikedImagesCopy
        })
      }
    } catch (error) {
      console.error('Error toggling like:', error)
    } finally {
      setIsLikeLoading(false)
    }
  }

  const getPrompt = () => {
    let prompt
    switch (i18n.language) {
      case 'ja':
        prompt = imageData?.prompt_ja
        break
      case 'de':
        prompt = imageData?.prompt_de
        break
      case 'zh':
      case 'zh-CN': // the code the site runs under (supported-locales.js)
        prompt = imageData?.prompt_zh
        break
      case 'es':
        prompt = imageData?.prompt_es
        break
      default:
        prompt = description
    }
    return prompt || description
  }

  const getNegativePrompt = () => {
    let negativePromptString
    switch (i18n.language) {
      case 'ja':
        negativePromptString = imageData?.negative_prompt_ja
        break
      case 'de':
        negativePromptString = imageData?.negative_prompt_de
        break
      case 'zh':
      case 'zh-CN': // the code the site runs under (supported-locales.js)
        negativePromptString = imageData?.negative_prompt_zh
        break
      case 'es':
        negativePromptString = imageData?.negative_prompt_es
        break
      default:
        negativePromptString = imageData?.negative_prompt
    }
    return negativePromptString || negativePrompt
  }
  // Safe Mode is on until it is known to be off: `safe` is null until the auth state
  // resolves (first paint, a slow or blocked sign-in script), and explicit items stay
  // blurred through that time. Only an account whose Safe Mode is off sees them unblurred.
  const nsfwBlur = nsfw && (safe !== false || signupOnly) ? 'blur-lg' : ''
  const optimizedLocalSrc = getOptimizedLocalSrc(editedImageUrl)
  const CardContainer = signupOnly ? Link : 'div'
  const ImageLink = signupOnly ? 'div' : Link
  const CardControl = signupOnly ? 'span' : 'button'

  const getModelIcon = (model) => {
    const isXL = model.includes('XL')
    const isXLPlus = model.includes('Pony')
    const isFlame = model.includes('Flame')

    return (
      <div className="relative inline-block">
        {/* Gold marks a CURRENT model, so v4-voice (version 5) earns it alongside v4 —
            otherwise the newest model reads as more downlevel than the one it sits beside.
            v2/v3 stay plain white as the legacy tiers. */}
        {imageData?.video ? (
          <div
            className={`text-xs font-black  ${
              isCurrentVideoVersion(imageData?.videoVersion)
                ? 'bg-gradient-to-b from-[#FFF4E6] to-[#F2AF50] text-transparent bg-clip-text'
                : 'text-white'
            } `}
            style={{ textShadow: '0 8px 12px rgba(0, 0, 0, 0.3)' }}
          >
            {/* The voice model writes `version: 5` (video3_service). Without an explicit branch
                it fell through the old ladder's catch-all and every v4-voice clip on Explore and
                Profile was labelled "v4.0" — the one model whose output is most obviously
                different, wearing the previous model's badge. */}
            {videoVersionLabel(imageData?.videoVersion)}
          </div>
        ) : (
          <div
            className={`text-xs font-black  ${
              isFlame
                ? 'bg-gradient-to-b from-[#E25822] to-[#FFCC33] text-transparent bg-clip-text'
                : isXLPlus
                  ? 'bg-gradient-to-b from-[#FF2A87] to-[#7C4EB7] text-transparent bg-clip-text'
                  : isXL
                    ? 'bg-gradient-to-b from-[#369BFD] to-[#2D2DD6] text-transparent bg-clip-text'
                    : 'text-gray-100'
            } `}
            style={{ textShadow: '0 8px 12px rgba(0, 0, 0, 0.2)' }}
          >
            {isFlame
              ? 'XL 2'
              : isXLPlus
                ? 'XL+'
                : isXL
                  ? 'XL'
                  : model
                    ? 'SD'
                    : 'AI'}
          </div>
        )}
      </div>
    )
  }

  if (isBroken) return null

  return (
    <CardContainer
      ref={containerRef}
      data-feed-id={id}
      href={signupOnly ? '/signup' : undefined}
      prefetch={signupOnly ? false : undefined}
      aria-label={
        signupOnly
          ? `Sign up to explore this ${imageData?.video ? 'video' : 'creation'}`
          : undefined
      }
      className={`overflow-hidden rounded-md ${signupOnly ? 'block group' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative  group overflow-hidden rounded-md shadow-md min-w-0">
        <div
          key={id}
          onClick={(e) => {
            if (signupOnly) return
            if (disabled) return
            if (onOpen) {
              e.preventDefault()
              e.stopPropagation()
              onOpen()
              return
            }
            const href = user ? `/image/${id}` : '/signup'
            // Always open in a new tab for the core card click:
            window.open(href, '_blank', 'noopener,noreferrer')
          }}
          className="cursor-pointer"
        >
          {/* Relative Container */}
          <div
            /* 1) Add “group” so all descendants can respond to :hover on this element */
            className="group relative w-full"
            style={{
              paddingTop:
                signupOnly && width > 0 && height > 0
                  ? `${(height / width) * 100}%`
                  : height === 512
                    ? '100%'
                    : '150%',
            }}
          >
            {/* Make sure overflowing content is clipped during the scale animation */}
            <div className="absolute inset-0 rounded-md overflow-hidden">
              {/* Invisible placeholder grid (no changes needed here) */}
              <div
                className={`grid absolute top-0 left-0 w-full h-full ${
                  imageLoaded ? 'hidden' : ''
                } rounded-md`}
                style={{
                  gridTemplateColumns: '2fr 3fr',
                  backgroundColor: 'var(--foreground-bg-color)',
                  zIndex: 1,
                }}
              ></div>

              {/* 2) Add transform/transition classes to <video> and <img> */}
              <ImageLink
                aria-label={
                  signupOnly
                    ? undefined
                    : disabled
                      ? () => {}
                      : !user
                        ? 'Sign up for Promptchan'
                        : 'Model image'
                }
                href={
                  signupOnly
                    ? undefined
                    : disabled
                      ? '#'
                      : !user
                        ? `/signup`
                        : `/image/${id}`
                }
                // Opens in a new tab, so a prefetch into this tab's router is never used
                // (one wasted image-page request per feed card).
                prefetch={signupOnly ? undefined : false}
                target={signupOnly ? undefined : '_blank'}
                rel={signupOnly ? undefined : 'noopener noreferrer'}
                onClick={(e) => {
                  if (signupOnly) return
                  if (onOpen && !disabled) {
                    e.preventDefault()
                    e.stopPropagation()
                    onOpen()
                  }
                }}
              >
                {signupOnly && imageData.video ? (
                  <>
                    <Image
                      src={posterSrc}
                      alt={title}
                      fill
                      loading="lazy"
                      sizes={imageSizes}
                      className={`object-cover object-center rounded-md ${nsfwBlur}`}
                      onLoad={handleImageLoad}
                      style={{ zIndex: 2 }}
                    />
                    <HoverVideoPreview
                      src={imageUrl}
                      className={`absolute inset-0 z-[3] h-full w-full rounded-md object-cover object-center ${nsfwBlur}`}
                    />
                    <div
                      className="pointer-events-none absolute right-2 top-2 z-[4] flex h-7 w-7 items-center justify-center rounded-full bg-black/65 ring-1 ring-white/60 shadow-[0_2px_10px_rgba(0,0,0,0.9)]"
                      aria-hidden="true"
                    >
                      <FaPlay className="translate-x-[1px] text-[11px] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]" />
                    </div>
                  </>
                ) : imageData.video ? (
                  autoplay ? (
                    // Featured/curated: one native <video> — the browser shows `poster`
                    // (the thumbnail) then hands off to the clip with no React state flip,
                    // so there's no grey pop-in. Autoplays; the play/pause effect pauses it
                    // off-screen. Kept visible since `poster` covers the load.
                    <video
                      ref={videoRef}
                      src={videoSrc}
                      poster={posterSrc || undefined}
                      className={`
      object-cover object-center w-full h-full
      absolute top-0 left-0 rounded-md
      transition-transform duration-300 ease-in-out transform
      opacity-100
      ${signupOnly ? '' : 'group-hover:scale-[1.02]'}
      ${nsfwBlur}
    `}
                      style={{ zIndex: 3 }}
                      onLoadedData={handleImageLoad}
                      onError={handleImageError}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <>
                      {/* First-frame poster (cheap WEBP `videoThumbnail`, or the hardened
                        `original_image_url`). Shown immediately so a video tile never
                        flashes a grey gap; on touch preview tiles this is the ONLY media
                        fetched. A broken thumbnail falls back to the clip's own frame. */}
                      {hasPoster && (
                        <img
                          src={posterSeen ? posterSrc : undefined}
                          fetchPriority={fetchPriority}
                          alt={title}
                          draggable={false}
                          onLoad={handleImageLoad}
                          onError={() => setPosterBroken(true)}
                          className={`
      object-cover object-center w-full h-full
      absolute top-0 left-0 rounded-md
      transition-transform duration-300 ease-in-out transform
      ${imageLoaded ? 'opacity-100' : 'opacity-0'}
      ${signupOnly ? '' : 'group-hover:scale-[1.02]'}
      ${nsfwBlur}
    `}
                          style={{ zIndex: 2 }}
                        />
                      )}
                      {/* The clip. With a poster it's fetched only to PLAY (in view for
                        autoplay grids, on hover for preview grids) and fades in over the
                        poster; with no poster it IS the poster — loaded on-screen, with
                        loadedmetadata nudging a paused tile to 0.05s so iOS renders the
                        first frame instead of a black box. */}
                      <video
                        ref={videoRef}
                        src={videoSrc}
                        className={`
      object-cover object-center w-full h-full
      absolute top-0 left-0 rounded-md
      transition duration-300 ease-in-out transform
      ${showClip ? 'opacity-100' : 'opacity-0'}
      ${signupOnly ? '' : 'group-hover:scale-[1.02]'}
      ${nsfwBlur}
    `}
                        style={{ zIndex: 3 }}
                        onLoadedMetadata={(e) => {
                          if (!hasPosterRef.current && !shouldPlayRef.current) {
                            try {
                              e.currentTarget.currentTime = 0.05
                            } catch {
                              /* ignore */
                            }
                          }
                        }}
                        onLoadedData={() => {
                          setVideoReady(true)
                          if (!hasPosterRef.current) handleImageLoad()
                        }}
                        onSeeked={() => {
                          if (!hasPosterRef.current) handleImageLoad()
                        }}
                        onError={() => {
                          if (!hasPosterRef.current) handleImageError()
                        }}
                        loop
                        muted
                        playsInline
                        preload="metadata"
                      />
                      {/* Play badge — marks a tile as a video when it isn't playing. */}
                      {showPlayBadge && (
                        <div
                          className="pointer-events-none absolute right-2 top-2 z-[4] flex h-7 w-7 items-center justify-center rounded-full bg-black/65 ring-1 ring-white/60 shadow-[0_2px_10px_rgba(0,0,0,0.9)]"
                          aria-hidden="true"
                        >
                          <FaPlay className="translate-x-[1px] text-[11px] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]" />
                        </div>
                      )}
                    </>
                  )
                ) : optimizedLocalSrc ? (
                  <Image
                    loading={shouldLazyLoad ? 'lazy' : 'eager'}
                    src={optimizedLocalSrc}
                    alt={title}
                    fill
                    sizes={imageSizes}
                    className={`
      object-cover object-center rounded-md
      transition-transform duration-300 ease-in-out transform
      ${imageLoaded ? 'opacity-100' : 'opacity-0'}
      ${signupOnly ? '' : 'group-hover:scale-[1.02]'}
      ${nsfwBlur}
    `}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ zIndex: 2 }}
                  />
                ) : (
                  <img
                    loading={shouldLazyLoad ? 'lazy' : 'eager'}
                    fetchPriority={fetchPriority}
                    src={shouldLoad ? editedImageUrl : undefined}
                    alt={title}
                    className={`
      object-cover object-center w-full h-full 
      absolute top-0 left-0 rounded-md
      transition-transform duration-300 ease-in-out transform
      ${imageLoaded ? 'opacity-100' : 'opacity-0'}
      ${signupOnly ? '' : 'group-hover:scale-[1.02]'}
      ${nsfwBlur}
    `}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ zIndex: 2 }}
                  />
                )}
              </ImageLink>
              {nsfwBlur && (
                <div
                  className="absolute inset-0 z-30 flex flex-col items-center justify-center pointer-events-none"
                  style={{ opacity: 0.8 }}
                >
                  <FaLock
                    aria-hidden="true"
                    className="text-white text-4xl mb-2"
                  />
                  <span className="text-white text-xs font-semibold">
                    {!user
                      ? 'Sign Up to View'
                      : safeCountry
                        ? 'Unavailable in Your Region'
                        : 'Safe Mode Enabled'}
                  </span>
                </div>
              )}
            </div>

            {imageLoaded && (
              <div
                className={`absolute bottom-0 left-0 w-full p-4 py-20 bg-gradient-to-t from-black/75 to-transparent z-10 transition-all duration-200 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto ${signupOnly ? 'group-focus-visible:opacity-100' : ''} ${
                  hideOverlaysMobile ? 'max-md:hidden' : ''
                }`}
              >
                <div className="text-white absolute bottom-12">
                  <p
                    data-card-prompt
                    data-landing-prompt={signupOnly ? '' : undefined}
                    title={getPrompt()}
                    className={`font-semibold hidden group-hover:block mt-1 text-sm px-2 ${signupOnly ? 'group-focus-visible:block' : ''}`}
                  >
                    {recommended
                      ? truncate(getPrompt() || '', 10)
                      : truncate(getPrompt() || '', 50)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {!recommended ? (
          <div className={`flex ${hideOverlaysMobile ? 'max-md:hidden' : ''}`}>
            {upscaled && (
              <CardControl
                onClick={signupOnly ? undefined : () => router.push('/upgrade')}
                aria-label="Upscaled"
                className="z-10 absolute top-8 left-2 flex items-center space-x-2 rounded-full px-4 py-1 text-xs pointer-events-none group-hover:pointer-events-auto transition-opacity"
              >
                <BsFillBadgeHdFill className="z-10 text-white shadow-lg" />
                <span className="z-10 text-white opacity-0 group-hover:opacity-100 transition-all duration-100 font-semibold">
                  Upscaled
                </span>
              </CardControl>
            )}

            {quality && (
              <CardControl
                onClick={
                  signupOnly
                    ? undefined
                    : () => {
                        if (selectedImageIds?.length > 0) {
                          return
                        }
                        router.push('/upgrade')
                      }
                }
                aria-label="Quality"
                className={`z-10 absolute top-1 left-2 flex items-center space-x-2 rounded-full px-4 py-1 text-xs pointer-events-none group-hover:pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity duration-100 ${
                  profile && 'left-8'
                }`}
              >
                <BsStack className="z-10 text-white shadow-lg" />
                <span className="z-10 text-white font-semibold text-xxs">
                  {t(quality.toLowerCase())}
                </span>
              </CardControl>
            )}

            <CardControl
              onClick={signupOnly ? undefined : () => handleModel(model)}
              aria-label={!model && !imageData?.video ? 'Image style' : 'Model'}
              className="z-10 absolute bottom-1 left-2 flex items-center rounded-full px-4 py-1 text-xs pointer-events-none group-hover:pointer-events-auto"
            >
              {/* Container with enough width to allow sliding */}
              <div className="relative w-32 h-5 flex-shrink-0 overflow-hidden">
                {/* Idle state: icons */}
                <span className="absolute inset-0 flex items-center gap-1 transition-all duration-300 ease-in-out transform opacity-100 translate-x-0 group-hover:translate-x-4 group-hover:opacity-0">
                  {getModelIcon(model)}

                  {imageData?.audio_enabled && (
                    <HiSpeakerWave className="w-3 h-3 text-white text-lg" />
                  )}
                </span>

                {/* Hover state: text */}
                <span className="absolute inset-0 flex items-center gap-1 text-white font-semibold text-xxs transition-all duration-300 ease-in-out transform opacity-0 -translate-x-4 group-hover:translate-x-0 group-hover:opacity-100 whitespace-nowrap">
                  {getModelName(
                    model,
                    imageData?.video,
                    imageData?.videoVersion,
                    false,
                  ) ||
                    imageData?.style ||
                    'AI image'}

                  {imageData?.audio_enabled && (
                    <>
                      <span className="opacity-60">·</span>
                      <span>Audio</span>
                    </>
                  )}
                </span>
              </div>
            </CardControl>

            {!hideMetrics && (
              <div className="absolute bottom-1 right-2 flex md:text-xs text-sm space-x-1 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-100 opacity-0 group-hover:opacity-100 px-3">
                <div className="z-10 group-hover:shadow-lg">
                  <CardControl
                    className="text-white rounded-md px-1 py-1 flex items-center space-x-1"
                    aria-label="Image views"
                  >
                    <span className="text-white">
                      <MdBarChart className="text-base" />
                    </span>
                    <span className="text-white text-xs font-semibold">
                      {formatNumber(views || 0)}
                    </span>
                  </CardControl>
                </div>

                <div
                  className="z-10 group-hover:shadow-lg hover:scale-[1.03]"
                  onClick={signupOnly ? undefined : toggleLike}
                >
                  <CardControl
                    className="text-white rounded-md px-1 py-1 flex items-center space-x-1 transition-all duration-100"
                    aria-label="Liked"
                  >
                    <span className="text-white">
                      {userHasLiked ? (
                        <FaHeart className="text-base text-[#ff2d55]" />
                      ) : (
                        <FaRegHeart className="text-base" />
                      )}
                    </span>
                    <span className="text-white text-xs font-semibold">
                      {formatNumber(currentLikes)}
                    </span>
                  </CardControl>
                </div>
              </div>
            )}

            {(user || signupOnly) && (
              <div
                className={`@container pointer-events-none absolute inset-0 z-10 flex items-start justify-end p-2 transition-opacity duration-100 ${
                  menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
              >
                {/* Buttons stay unclickable until revealed so taps on the corner
                    of an idle card still open the image, as before. */}
                <div
                  className={`flex items-center gap-2 ${
                    menuOpen
                      ? 'pointer-events-auto'
                      : 'pointer-events-none group-hover:pointer-events-auto'
                  }`}
                >
                  {imageData?.video ? (
                    <CardControl
                      type={signupOnly ? undefined : 'button'}
                      title={t('extend')}
                      aria-label="Extend"
                      className={OVERLAY_PILL}
                      onClick={signupOnly ? undefined : handleExtend}
                    >
                      <FaAngleDoubleRight className="text-base" />
                      <span className="hidden @[380px]:inline">
                        {t('extend')}
                      </span>
                    </CardControl>
                  ) : (
                    <CardControl
                      type={signupOnly ? undefined : 'button'}
                      title={t('clone')}
                      aria-label="Clone"
                      className={OVERLAY_PILL}
                      onClick={signupOnly ? undefined : handleClone}
                    >
                      <FaMagic className="text-base" />
                      <span className="hidden @[380px]:inline">
                        {t('clone')}
                      </span>
                    </CardControl>
                  )}

                  <OverlayActionsMenu
                    portal
                    presentationOnly={signupOnly}
                    onOpenChange={setMenuOpen}
                    items={
                      signupOnly
                        ? []
                        : [
                            ...(imageData?.video
                              ? [
                                  {
                                    key: 'clone',
                                    icon: <FaMagic className="text-sm" />,
                                    label: t('clone'),
                                    onClick: handleClone,
                                  },
                                ]
                              : [
                                  {
                                    key: 'animate',
                                    icon: <FaFilm className="text-sm" />,
                                    label: t('animate'),
                                    onClick: useAnimate,
                                  },
                                  {
                                    key: 'character',
                                    icon: (
                                      <BsPersonBoundingBox className="text-sm" />
                                    ),
                                    label: t('useCharacter'),
                                    onClick: useCharacter,
                                  },
                                  {
                                    key: 'chat',
                                    icon: (
                                      <BsChatDotsFill className="text-sm" />
                                    ),
                                    label: t('chatWithThisImage'),
                                    onClick: useChat,
                                  },
                                  {
                                    key: 'tweak',
                                    icon: <FaMarker className="text-sm" />,
                                    label: t('tweak', 'Tweak'),
                                    onClick: handleTweak,
                                  },
                                ]),
                            ...(uid === user?.uid || canRemoveOthers
                              ? [
                                  {
                                    key: 'delete',
                                    icon: <FaTrash className="text-sm" />,
                                    label: t('delete'),
                                    destructive: true,
                                    loading: isDeleting,
                                    onClick: deleteImage,
                                  },
                                ]
                              : []),
                          ]
                    }
                  />
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Selection checkbox lives OUTSIDE the overlay block above, so it stays
            visible on small screens (where that block is hidden via
            hideOverlaysMobile). Absolute positioning is unchanged — it still
            anchors to the card body. */}
        {profile && (
          <div
            className={`absolute top-1 left-1 z-20 ${
              isChecked
                ? 'block'
                : 'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity'
            }`}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxToggle}
              className="form-checkbox h-8 w-8 accent-indigo-500"
            />
          </div>
        )}
      </div>
    </CardContainer>
  )
}

export default Card
